/**
 * Ad naming convention rendering (UX plan §4.5, decision #4).
 *
 * Template tokens: [Brand] [Angle] [Hook] [Format] [v] [date]. The template
 * itself is a project-level platform setting with a one-off batch override;
 * this renderer is intentionally deterministic so names match between the
 * launch preview, the exported bulk sheet, and the Meta push.
 */

import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { formatBadge, getItemAngleId, hookLine, type AdCreativeAngle } from './batchModel';

export const DEFAULT_NAMING_CONVENTION = '[Brand]_[Angle]_[Hook]_[Format]_[v]_[date]';

export interface NamingContext {
  brand: string;
  batchDate?: Date;
  angles: AdCreativeAngle[];
  version?: number;
  /** Exact authored locale selected for this deployment. */
  locale?: string;
}

export function slugToken(value: string, maxLength = 24): string {
  const cleaned = value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const trimmed = cleaned.length > maxLength
    ? cleaned.slice(0, maxLength).replace(/-+$/g, '')
    : cleaned;
  return trimmed || 'na';
}

export function renderAdName(
  template: string,
  item: AdCreativeItem,
  context: NamingContext,
): string {
  const angle = context.angles.find((candidate) => candidate.id === getItemAngleId(item));
  const date = context.batchDate ?? asDate(item.createdAt) ?? new Date();
  const tokens: Record<string, string> = {
    Brand: slugToken(context.brand, 20),
    Angle: slugToken(angle?.label ?? 'general', 20),
    Hook: slugToken(hookLine(item, context.locale ?? item.defaultLanguage, 'strict'), 28),
    Format: slugToken(formatBadge(item), 12),
    v: `v${context.version ?? 1}`,
    date: formatDateToken(date),
  };
  return (template || DEFAULT_NAMING_CONVENTION).replace(/\[([A-Za-z]+)\]/g, (match, token: string) =>
    tokens[token] ?? match,
  );
}

export function buildUtm(options: {
  batchTitle: string;
  adNamePlaceholder?: string;
}): { utmSource: string; utmMedium: string; utmCampaign: string; utmContent: string } {
  return {
    utmSource: 'meta',
    utmMedium: 'paid_social',
    utmCampaign: slugToken(options.batchTitle, 40),
    utmContent: options.adNamePlaceholder ?? '{{ad.name}}',
  };
}

export function appendUtmToUrl(url: string, utm: Record<string, string>): string {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    Object.entries({
      utm_source: utm.utmSource,
      utm_medium: utm.utmMedium,
      utm_campaign: utm.utmCampaign,
      utm_content: utm.utmContent,
    }).forEach(([key, value]) => {
      if (value) parsed.searchParams.set(key, value);
    });
    return parsed.toString();
  } catch {
    return url;
  }
}

function formatDateToken(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

function asDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value && typeof value === 'object' && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}
